/**
 * DocumentsCard - right-rail container, directly under Properties, that
 * lists every (non-inline) document on the ticket as tiles styled like
 * the Recently-viewed tiles. Collapsed by default.
 *
 * Each document tile shows name, type, size, added date, and added-by,
 * plus an optional free-text description (inline-editable by the agent),
 * and the same three actions the inline AttachmentTile offers: Download,
 * Attach to Response, Delete.
 *
 * A distinct Manifest tile sits at the top. It opens a modal showing the
 * always-current JSON manifest an AI agent reads over the public API, so
 * a human can see exactly what the AI sees and copy it. The manifest is
 * derived live by the backend - it cannot drift from the actual files.
 */
import { useState } from "react";

import { Button, Card, Modal, Spinner } from "@/components/ui";
import {
  fetchAgentAttachmentDownloadUrl,
  useAgentTicketAttachmentManifest,
  useSetAttachmentDescription,
} from "@/hooks/useAgentTicketDetail";
import type { AttachmentResponse } from "@/types/api";

const UPLOADER_FALLBACK = "API / automation";

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Short, human "type" label. Prefers the filename extension (PDF, XLSX,
 * CSV, ...) and falls back to the raw content_type. */
function typeLabel(filename: string, contentType: string): string {
  const dot = filename.lastIndexOf(".");
  if (dot >= 0 && dot < filename.length - 1) {
    return filename.slice(dot + 1).toUpperCase();
  }
  return contentType || "file";
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function iconForType(contentType: string): string {
  if (contentType.startsWith("image/")) return "fa-image";
  if (contentType === "application/pdf") return "fa-file-pdf";
  if (contentType.includes("spreadsheet") || contentType === "text/csv") {
    return "fa-file-spreadsheet";
  }
  if (contentType.startsWith("text/")) return "fa-file-lines";
  return "fa-file";
}

interface DocumentTileProps {
  ticketNumber: number;
  attachment: AttachmentResponse;
  onAttachToResponse: (attachment: AttachmentResponse) => void;
  onRemove: (attachment: AttachmentResponse) => void;
  isAttachedToDraft: boolean;
}

function DocumentTile({
  ticketNumber,
  attachment,
  onAttachToResponse,
  onRemove,
  isAttachedToDraft,
}: DocumentTileProps): JSX.Element {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draftDescription, setDraftDescription] = useState(
    attachment.description ?? "",
  );
  const setDescription = useSetAttachmentDescription(ticketNumber);

  async function handleDownload(): Promise<void> {
    setError(null);
    setIsDownloading(true);
    try {
      const url = await fetchAgentAttachmentDownloadUrl(
        ticketNumber,
        attachment.id,
      );
      // Content-Disposition: attachment on the signed URL makes this
      // download rather than navigate.
      const a = document.createElement("a");
      a.href = url;
      a.download = attachment.filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setError("Could not get a download URL for this document.");
    } finally {
      setIsDownloading(false);
    }
  }

  async function saveDescription(): Promise<void> {
    const next = draftDescription.trim();
    try {
      await setDescription.mutateAsync({
        attachmentId: attachment.id,
        description: next.length > 0 ? next : null,
      });
      setIsEditing(false);
    } catch {
      setError("Could not save the description.");
    }
  }

  const uploadedBy = attachment.uploaded_by_name || UPLOADER_FALLBACK;

  return (
    <div
      className="rounded-md border border-neutral-200 bg-white p-2.5 transition-colors hover:border-primary-300 hover:bg-primary-50/40"
      data-testid="document-tile"
    >
      <div className="flex items-start gap-2.5">
        <i
          className={`fa-duotone fa-thin ${iconForType(attachment.content_type)} mt-0.5 text-base text-neutral-500`}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <div
            className="truncate text-sm font-medium text-neutral-800"
            title={attachment.filename}
          >
            {attachment.filename}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-neutral-500">
            <span className="font-medium uppercase tracking-wide">
              {typeLabel(attachment.filename, attachment.content_type)}
            </span>
            <span aria-hidden="true">&middot;</span>
            <span>{formatBytes(attachment.byte_size)}</span>
            {attachment.created_at && (
              <>
                <span aria-hidden="true">&middot;</span>
                <span>{formatDate(attachment.created_at)}</span>
              </>
            )}
            <span aria-hidden="true">&middot;</span>
            <span className="truncate" title={`Added by ${uploadedBy}`}>
              {uploadedBy}
            </span>
          </div>
        </div>
        {/* Actions: Download, Attach to Response, Delete - same trio as
            the inline attachment chips. */}
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => {
              void handleDownload();
            }}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
            title={`Download ${attachment.filename}`}
            aria-label={`Download ${attachment.filename}`}
            disabled={isDownloading}
          >
            <i
              className={`fa-duotone fa-thin ${isDownloading ? "fa-spinner-third fa-spin" : "fa-download"} text-sm`}
              aria-hidden="true"
            />
          </button>
          <button
            type="button"
            onClick={() => onAttachToResponse(attachment)}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
            title={
              isAttachedToDraft
                ? `Already attached to your reply: ${attachment.filename}`
                : `Attach ${attachment.filename} to your reply`
            }
            aria-label={`Attach ${attachment.filename} to your reply`}
          >
            <i
              className={`fa-duotone fa-thin ${isAttachedToDraft ? "fa-paperclip-vertical text-primary-600" : "fa-paperclip"} text-sm`}
              aria-hidden="true"
            />
          </button>
          <button
            type="button"
            onClick={() => onRemove(attachment)}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-neutral-500 hover:bg-red-50 hover:text-red-600"
            title={`Delete ${attachment.filename}`}
            aria-label={`Delete ${attachment.filename}`}
          >
            <i className="fa-duotone fa-thin fa-trash text-sm" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Description - inline editable. Drives the manifest entry so an
          AI agent can triage the file without downloading it. */}
      <div className="mt-2 pl-[26px]">
        {isEditing ? (
          <div className="space-y-1.5">
            <textarea
              value={draftDescription}
              onChange={(e) => setDraftDescription(e.target.value)}
              rows={2}
              maxLength={4000}
              placeholder="Describe what this file is, for AI agents and teammates."
              aria-label={`Description for ${attachment.filename}`}
              data-testid="document-description-input"
              className="w-full rounded border border-neutral-300 px-2 py-1 text-xs text-neutral-800 outline-none focus:border-primary-400"
            />
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="primary"
                onClick={() => {
                  void saveDescription();
                }}
                isLoading={setDescription.isPending}
              >
                Save
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setDraftDescription(attachment.description ?? "");
                  setIsEditing(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="block w-full text-left text-xs text-neutral-600 hover:text-neutral-800"
            data-testid="document-description"
          >
            {attachment.description ? (
              attachment.description
            ) : (
              <span className="text-neutral-400">
                <i className="fa-duotone fa-thin fa-pen mr-1" aria-hidden="true" />
                Add a description
              </span>
            )}
          </button>
        )}
      </div>

      {error && <div className="mt-1 text-xs text-red-600">{error}</div>}
    </div>
  );
}

interface ManifestTileProps {
  ticketNumber: number;
  documentCount: number;
  totalByteSize: number;
}

function ManifestTile({
  ticketNumber,
  documentCount,
  totalByteSize,
}: ManifestTileProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const manifest = useAgentTicketAttachmentManifest(ticketNumber, {
    enabled: open,
  });

  const json = manifest.data
    ? JSON.stringify(manifest.data, null, 2)
    : "";

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-2.5 rounded-md border border-dashed border-primary-300 bg-primary-50/50 p-2.5 text-left transition-colors hover:bg-primary-50"
        data-testid="manifest-tile"
      >
        <i
          className="fa-duotone fa-thin fa-list-tree text-base text-primary-600"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-neutral-800">
            Document manifest
          </div>
          <div className="text-xs text-neutral-500">
            {documentCount} {documentCount === 1 ? "file" : "files"}
            {", "}
            {formatBytes(totalByteSize)} - what an AI agent reads
          </div>
        </div>
        <i
          className="fa-duotone fa-thin fa-arrow-up-right-from-square text-sm text-neutral-400"
          aria-hidden="true"
        />
      </button>

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title="Document manifest"
        size="lg"
      >
        <p className="mb-3 text-sm text-neutral-600">
          AI agents fetch this over the public API at
          {" "}
          <code className="rounded bg-neutral-100 px-1 py-0.5 text-xs">
            GET /api/v1/public/tickets/{"{ticket_ref}"}/attachments/manifest
          </code>
          . It is always current - derived live from the files on this
          ticket - so an agent can decide what to download without
          fetching every file.
        </p>
        {manifest.isLoading && (
          <div className="flex items-center justify-center py-8">
            <Spinner />
          </div>
        )}
        {manifest.isError && (
          <div className="text-sm text-red-600">
            Could not load the manifest.
          </div>
        )}
        {manifest.data && (
          <>
            <div className="mb-2 flex justify-end">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  void copy();
                }}
              >
                <i
                  className="fa-duotone fa-thin fa-copy mr-1.5"
                  aria-hidden="true"
                />
                {copied ? "Copied" : "Copy JSON"}
              </Button>
            </div>
            <pre
              className="max-h-[50vh] overflow-auto rounded bg-neutral-900 p-3 text-xs leading-relaxed text-neutral-100"
              data-testid="manifest-json"
            >
              {json}
            </pre>
          </>
        )}
      </Modal>
    </>
  );
}

export interface DocumentsCardProps {
  ticketNumber: number;
  attachments: AttachmentResponse[];
  onAttachToResponse: (attachment: AttachmentResponse) => void;
  onRemove: (attachment: AttachmentResponse) => void;
  /** Ids of attachments currently staged on the reply (paperclip state). */
  attachedDraftIds?: ReadonlySet<string>;
}

export function DocumentsCard({
  ticketNumber,
  attachments,
  onAttachToResponse,
  onRemove,
  attachedDraftIds,
}: DocumentsCardProps): JSX.Element {
  // Collapsed by default (Greg's requirement). Persist nothing - the
  // rail is a context aid and a fresh page load starts collapsed.
  const [expanded, setExpanded] = useState(false);

  const totalBytes = attachments.reduce((sum, a) => sum + (a.byte_size || 0), 0);
  const count = attachments.length;

  return (
    <Card>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2"
        aria-expanded={expanded}
        data-testid="documents-card-toggle"
      >
        <i
          className="fa-duotone fa-thin fa-folder-open text-base text-neutral-500"
          aria-hidden="true"
        />
        <h2 className="text-sm font-semibold text-neutral-700">Documents</h2>
        <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600">
          {count}
        </span>
        <i
          className={`fa-duotone fa-thin fa-chevron-${expanded ? "up" : "down"} ml-auto text-sm text-neutral-400`}
          aria-hidden="true"
        />
      </button>

      {expanded && (
        <div className="mt-3 space-y-2" data-testid="documents-card-body">
          <ManifestTile
            ticketNumber={ticketNumber}
            documentCount={count}
            totalByteSize={totalBytes}
          />
          {count === 0 ? (
            <p className="px-1 text-xs text-neutral-500">
              No documents on this ticket yet.
            </p>
          ) : (
            attachments.map((a) => (
              <DocumentTile
                key={a.id}
                ticketNumber={ticketNumber}
                attachment={a}
                onAttachToResponse={onAttachToResponse}
                onRemove={onRemove}
                isAttachedToDraft={attachedDraftIds?.has(a.id) ?? false}
              />
            ))
          )}
        </div>
      )}
    </Card>
  );
}

export default DocumentsCard;
